import { useVehicleValuationCostWarrantyCreateMutation } from "@/common/api/vehicle-valuation-cost-warranty/use-vehicle-valuation-cost-warranty-create.mutation";
import { useVehicleValuationCostWarrantyDeleteMutation } from "@/common/api/vehicle-valuation-cost-warranty/use-vehicle-valuation-cost-warranty-delete.mutation";
import { useVehicleValuationCostWarrantySearchQuery } from "@/common/api/vehicle-valuation-cost-warranty/use-vehicle-valuation-cost-warranty-search.query";
import { useVehicleValuationCostWarrantyUpdateMutation } from "@/common/api/vehicle-valuation-cost-warranty/use-vehicle-valuation-cost-warranty-update.mutation";
import type { VehicleValuationCostWarranty } from "@/common/api/vehicle-valuation-cost-warranty/vehicle-valuation-cost-warranty.types";
import { handleFormSubmission } from "@/common/helpers/form-submission";
import { createCurrencyFormatter } from "@/common/helpers/format-currency";
import { useNumberFormatLocale } from "@/common/helpers/number-format";
import { DeleteConfirmDialog } from "@/components/confirm-dialog/predefined/delete-confirm-dialog";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InputTextAlign } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { LocalizedNumberInput } from "@/components/ui/localized-number-input";
import { PencilIcon, PlusIcon, SaveIcon, Trash2Icon, XIcon } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  EMPTY_WARRANTY_FORM,
  type WarrantyFormState,
  isNonNegativeNumber,
  isOptionalNonNegativeInteger,
  parseNonNegativeNumber,
  parseOptionalNonNegativeInteger,
  sortWarrantyBrackets,
  toWarrantyFormState,
} from "../_helpers/fab-valuation-settings.helpers";
import { WarrantyListSkeleton } from "./fab-valuation-warranty-skeleton";

const ROW_GRID = "grid grid-cols-[minmax(0,1fr)_11rem_5.5rem] items-center gap-5";

export function FabValuationWarranty(): ReactElement {
  const { t } = useTranslation("fab");
  const numberFormatLocale = useNumberFormatLocale();
  const warrantySearchQuery = useVehicleValuationCostWarrantySearchQuery({
    options: { parameters: { pageSize: -1 } },
  });
  const warrantyCreateMutation = useVehicleValuationCostWarrantyCreateMutation();
  const warrantyUpdateMutation = useVehicleValuationCostWarrantyUpdateMutation();
  const warrantyDeleteMutation = useVehicleValuationCostWarrantyDeleteMutation();

  const [newWarranty, setNewWarranty] = useState<WarrantyFormState>(EMPTY_WARRANTY_FORM);
  const [editingWarrantyUuid, setEditingWarrantyUuid] = useState<string | null>(null);
  const [editingWarranty, setEditingWarranty] = useState<WarrantyFormState>(EMPTY_WARRANTY_FORM);
  const [warrantyToDelete, setWarrantyToDelete] = useState<VehicleValuationCostWarranty | null>(null);
  const [activeWarrantyAction, setActiveWarrantyAction] = useState<string | null>(null);

  const warranties = useMemo(
    () => sortWarrantyBrackets(warrantySearchQuery.data?.data ?? []),
    [warrantySearchQuery.data?.data],
  );

  const currencyFormatter = useMemo(
    () => createCurrencyFormatter({ locale: numberFormatLocale, fractionDigits: 2 }),
    [numberFormatLocale],
  );
  const kilowattFormatter = useMemo(
    () =>
      new Intl.NumberFormat(numberFormatLocale, {
        maximumFractionDigits: 0,
      }),
    [numberFormatLocale],
  );

  const isNewWarrantyUpToKilowattsValid = isOptionalNonNegativeInteger(newWarranty.upToKilowatts);
  const isNewWarrantyPriceValid = isNonNegativeNumber(newWarranty.price);
  const isEditingWarrantyUpToKilowattsValid = isOptionalNonNegativeInteger(editingWarranty.upToKilowatts);
  const isEditingWarrantyPriceValid = isNonNegativeNumber(editingWarranty.price);
  const showNewWarrantyUpToKilowattsError =
    newWarranty.upToKilowatts.trim().length > 0 && !isNewWarrantyUpToKilowattsValid;
  const showNewWarrantyPriceError = newWarranty.price.trim().length > 0 && !isNewWarrantyPriceValid;
  const showEditingWarrantyUpToKilowattsError =
    editingWarranty.upToKilowatts.trim().length > 0 && !isEditingWarrantyUpToKilowattsValid;
  const showEditingWarrantyPriceError = editingWarranty.price.trim().length > 0 && !isEditingWarrantyPriceValid;

  function getWarrantyDisplayName(warranty: Pick<VehicleValuationCostWarranty, "price" | "upToKilowatts">): string {
    const limitLabel =
      warranty.upToKilowatts == null
        ? t("CatchAll")
        : t("UpToKilowattsValue", { value: kilowattFormatter.format(warranty.upToKilowatts) });
    return `${limitLabel} · ${currencyFormatter.format(warranty.price)}`;
  }

  function handleNewWarrantyChange(field: keyof WarrantyFormState, value: string): void {
    setNewWarranty((currentWarranty) => ({ ...currentWarranty, [field]: value }));
  }

  function handleStartWarrantyEdit(warranty: VehicleValuationCostWarranty): void {
    setEditingWarrantyUuid(warranty.uuid);
    setEditingWarranty(toWarrantyFormState(warranty));
  }

  function handleEditingWarrantyChange(field: keyof WarrantyFormState, value: string): void {
    setEditingWarranty((currentWarranty) => ({ ...currentWarranty, [field]: value }));
  }

  function handleCancelWarrantyEdit(): void {
    setEditingWarrantyUuid(null);
    setEditingWarranty(EMPTY_WARRANTY_FORM);
  }

  async function handleCreateWarranty(): Promise<void> {
    if (!isNewWarrantyPriceValid || !isNewWarrantyUpToKilowattsValid) return;

    setActiveWarrantyAction("create");
    await handleFormSubmission(
      () =>
        warrantyCreateMutation.mutateAsync({
          upToKilowatts: parseOptionalNonNegativeInteger(newWarranty.upToKilowatts),
          price: parseNonNegativeNumber(newWarranty.price),
        }),
      {
        success: t("WarrantyCreated"),
        error: t("WarrantyCreateError"),
        customErrors: { 409: t("WarrantyConflict") },
      },
      () => {
        setNewWarranty(EMPTY_WARRANTY_FORM);
      },
    );
    setActiveWarrantyAction(null);
  }

  async function handleUpdateWarranty(): Promise<void> {
    if (!editingWarrantyUuid || !isEditingWarrantyPriceValid || !isEditingWarrantyUpToKilowattsValid) return;

    setActiveWarrantyAction(`update:${editingWarrantyUuid}`);
    await handleFormSubmission(
      () =>
        warrantyUpdateMutation.mutateAsync({
          uuid: editingWarrantyUuid,
          upToKilowatts: parseOptionalNonNegativeInteger(editingWarranty.upToKilowatts),
          price: parseNonNegativeNumber(editingWarranty.price),
        }),
      {
        success: t("WarrantySaved"),
        error: t("WarrantySaveError"),
        customErrors: { 409: t("WarrantyConflict") },
      },
      () => {
        handleCancelWarrantyEdit();
      },
    );
    setActiveWarrantyAction(null);
  }

  async function handleDeleteWarranty(): Promise<void> {
    if (!warrantyToDelete) return;

    setActiveWarrantyAction(`delete:${warrantyToDelete.uuid}`);
    await handleFormSubmission(
      () => warrantyDeleteMutation.mutateAsync({ uuid: warrantyToDelete.uuid }),
      { success: t("WarrantyDeleted"), error: t("WarrantyDeleteError") },
      () => {
        if (editingWarrantyUuid === warrantyToDelete.uuid) {
          handleCancelWarrantyEdit();
        }

        setWarrantyToDelete(null);
      },
    );
    setActiveWarrantyAction(null);
  }

  return (
    <section className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("WarrantyPrices")}</CardTitle>
          <CardDescription>{t("WarrantyPricesDescription")}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {warrantySearchQuery.isLoading && !warrantySearchQuery.data ? (
            <WarrantyListSkeleton />
          ) : warrantySearchQuery.isError && !warrantySearchQuery.data ? (
            <p className="text-error text-sm">{t("WarrantyPricesLoadError")}</p>
          ) : (
            <div className="space-y-1.5">
              <div className={`${ROW_GRID}`}>
                <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
                  {t("KilowattsRange")}
                </p>
                <p className="text-muted-foreground text-right text-xs font-medium tracking-wider uppercase">
                  {t("Price")}
                </p>
                <div />
              </div>

              {warranties.length === 0 ? (
                <p className="text-muted-foreground py-4 text-center text-sm">{t("NoWarrantyBracketsConfigured")}</p>
              ) : null}

              {warranties.map((warranty) => {
                const isEditing = editingWarrantyUuid === warranty.uuid;
                const isUpdating =
                  activeWarrantyAction === `update:${warranty.uuid}` && warrantyUpdateMutation.isPending;
                const isDeleting =
                  activeWarrantyAction === `delete:${warranty.uuid}` && warrantyDeleteMutation.isPending;

                return (
                  <div className={`${ROW_GRID} py-0.5`} key={warranty.uuid}>
                    <div>
                      {isEditing ? (
                        <>
                          <label className="sr-only" htmlFor={`warranty-limit-${warranty.uuid}`}>
                            {t("KilowattsRange")}
                          </label>
                          <LocalizedNumberInput
                            id={`warranty-limit-${warranty.uuid}`}
                            min="0"
                            integerOnly
                            placeholder={t("UpToKilowattsHint")}
                            error={showEditingWarrantyUpToKilowattsError}
                            value={editingWarranty.upToKilowatts}
                            onValueChange={(value) => handleEditingWarrantyChange("upToKilowatts", value)}
                          />
                        </>
                      ) : (
                        <span className="text-sm">
                          {warranty.upToKilowatts == null
                            ? t("CatchAll")
                            : t("UpToKilowattsValue", { value: kilowattFormatter.format(warranty.upToKilowatts) })}
                        </span>
                      )}
                    </div>
                    <div>
                      {isEditing ? (
                        <>
                          <label className="sr-only" htmlFor={`warranty-price-${warranty.uuid}`}>
                            {t("WarrantyPrice")}
                          </label>
                          <LocalizedNumberInput
                            id={`warranty-price-${warranty.uuid}`}
                            min="0"
                            maxFractionDigits={2}
                            prefix="€"
                            textAlign={InputTextAlign.Right}
                            error={showEditingWarrantyPriceError}
                            value={editingWarranty.price}
                            onValueChange={(value) => handleEditingWarrantyChange("price", value)}
                          />
                          {showEditingWarrantyPriceError ? (
                            <p className="text-error text-xs">{t("EnterValidNumber")}</p>
                          ) : null}
                        </>
                      ) : (
                        <span className="block text-right text-sm">{currencyFormatter.format(warranty.price)}</span>
                      )}
                    </div>
                    <div className="flex justify-end gap-2">
                      {isEditing ? (
                        <>
                          <Button
                            type="button"
                            size={ButtonSize.Sm}
                            disabled={
                              isUpdating || !isEditingWarrantyPriceValid || !isEditingWarrantyUpToKilowattsValid
                            }
                            onClick={() => void handleUpdateWarranty()}
                          >
                            {isUpdating ? (
                              <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor />
                            ) : (
                              <SaveIcon />
                            )}
                          </Button>
                          <Button
                            type="button"
                            size={ButtonSize.Sm}
                            variant={ButtonVariant.Outlined}
                            colorVariant={ButtonColorVariant.Secondary}
                            disabled={isUpdating}
                            onClick={handleCancelWarrantyEdit}
                          >
                            <XIcon />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            type="button"
                            size={ButtonSize.Sm}
                            variant={ButtonVariant.Outlined}
                            colorVariant={ButtonColorVariant.Secondary}
                            disabled={activeWarrantyAction != null || isDeleting}
                            onClick={() => handleStartWarrantyEdit(warranty)}
                          >
                            <PencilIcon />
                          </Button>
                          <Button
                            type="button"
                            size={ButtonSize.Sm}
                            variant={ButtonVariant.Outlined}
                            colorVariant={ButtonColorVariant.Error}
                            disabled={activeWarrantyAction != null || isDeleting}
                            onClick={() => setWarrantyToDelete(warranty)}
                          >
                            {isDeleting ? (
                              <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                            ) : (
                              <Trash2Icon />
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}

              <div className={`${ROW_GRID} py-0.5`}>
                <div>
                  <label className="sr-only" htmlFor="newWarrantyUpToKilowatts">
                    {t("KilowattsRange")}
                  </label>
                  <LocalizedNumberInput
                    id="newWarrantyUpToKilowatts"
                    min="0"
                    integerOnly
                    placeholder={t("UpToKilowattsHint")}
                    error={showNewWarrantyUpToKilowattsError}
                    value={newWarranty.upToKilowatts}
                    onValueChange={(value) => handleNewWarrantyChange("upToKilowatts", value)}
                  />
                </div>
                <div>
                  <label className="sr-only" htmlFor="newWarrantyPrice">
                    {t("WarrantyPrice")}
                  </label>
                  <LocalizedNumberInput
                    id="newWarrantyPrice"
                    min="0"
                    maxFractionDigits={2}
                    prefix="€"
                    textAlign={InputTextAlign.Right}
                    error={showNewWarrantyPriceError}
                    value={newWarranty.price}
                    onValueChange={(value) => handleNewWarrantyChange("price", value)}
                  />
                  {showNewWarrantyPriceError ? <p className="text-error text-xs">{t("EnterValidNumber")}</p> : null}
                </div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    size={ButtonSize.Sm}
                    disabled={
                      !isNewWarrantyUpToKilowattsValid ||
                      !isNewWarrantyPriceValid ||
                      (activeWarrantyAction === "create" && warrantyCreateMutation.isPending)
                    }
                    onClick={() => void handleCreateWarranty()}
                  >
                    {activeWarrantyAction === "create" && warrantyCreateMutation.isPending ? (
                      <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor />
                    ) : (
                      <PlusIcon />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>

        {warrantyToDelete ? (
          <DeleteConfirmDialog
            open
            label={t("WarrantyBracket")}
            name={getWarrantyDisplayName(warrantyToDelete)}
            onCancel={() => setWarrantyToDelete(null)}
            onConfirm={() => void handleDeleteWarranty()}
          />
        ) : null}
      </Card>
    </section>
  );
}
